import {
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { CgSpinner } from 'react-icons/cg';
import { cn } from '@/utils/cn';
import { MesAno } from '@/utils/MesAno';
import { Button } from '../atoms/Button';
import SimpleSelect from '../atoms/SimpleSelect';

interface NewRegulatoryTrainingMatrixAuditModalProps {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	onConfirmation: (
		monthAndYear: MesAno,
		allSinceMayOf2026: boolean,
		allBeforeMayOf2026: boolean,
	) => void;
	isLoading: boolean;
}

export default function NewRegulatoryTrainingMatrixAuditModal({
	isOpen,
	setIsOpen,
	onConfirmation,
	isLoading,
}: NewRegulatoryTrainingMatrixAuditModalProps) {
	const [everythingUntilMayOf26, setEverythingUntilMayOf26] = useState(false);
	const [everythingSinceMayOf26, setEverythingSinceMayOf26] = useState(false);
	const [monthAndYear, setMonthAndYear] = useState<MesAno>(
		new MesAno(new Date().getMonth() + 1, new Date().getFullYear()),
	);

	const minimumYearForSelect = 2026;
	const currentYear = new Date().getFullYear();
	const availableYearsForSelect = Array.from(
		{ length: currentYear - minimumYearForSelect + 1 },
		(_, i) => minimumYearForSelect + i,
	);

	const availableMonthsForSelect = useMemo(() => {
		const minimumMonth = monthAndYear.year === 2026 ? 5 : 1;
		const maximumMonth =
			monthAndYear.year === currentYear ? new Date().getMonth() + 1 : 12;
		return Array.from(
			{ length: maximumMonth - (minimumMonth - 1) },
			(_, i) => minimumMonth + i,
		);
	}, [monthAndYear.year, currentYear]);

	const isValidSelection =
		everythingUntilMayOf26 ||
		everythingSinceMayOf26 ||
		(monthAndYear.month &&
			availableMonthsForSelect.includes(monthAndYear.month) &&
			monthAndYear.year &&
			availableYearsForSelect.includes(monthAndYear.year));

	return (
		<Dialog
			PaperProps={{
				style: {
					borderRadius: '20px',
					boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
					overflowY: 'unset',
					width: '35rem',
				},
			}}
			open={isOpen}
			onClose={() => setIsOpen(false)}
		>
			<DialogTitle sx={{ paddingX: '2rem' }}>
				<span className="font-graphie text-[19px] font-bold text-slate-800">
					Relatório de Alterações da Matriz
				</span>
			</DialogTitle>
			<DialogContent sx={{ paddingX: '2rem' }}>
				<span className="text-[19px] mb-4 block">
					Selecione o mês de extração do Relatório de Alterações da Matriz
				</span>
				<div className="flex gap-4 justify-between mb-4">
					<SimpleSelect
						title="Ano"
						className="flex-1"
						width="100%"
						fontSize="1rem"
						labelFontSize=".75rem"
						selectSx={{ height: '2.5rem', borderRadius: '8px' }}
						disabled={everythingUntilMayOf26 || everythingSinceMayOf26}
						value={monthAndYear.year}
						setState={(year) =>
							setMonthAndYear(new MesAno(monthAndYear.month, Number(year)))
						}
						values={availableYearsForSelect.map((year) => ({
							value: year,
							label: year,
						}))}
					/>
					<SimpleSelect
						title="Mês"
						className="flex-1"
						width="100%"
						fontSize="1rem"
						labelFontSize=".75rem"
						selectSx={{ height: '2.5rem', borderRadius: '8px' }}
						disabled={everythingUntilMayOf26 || everythingSinceMayOf26}
						value={monthAndYear.month}
						setState={(month) =>
							setMonthAndYear(new MesAno(Number(month), monthAndYear.year))
						}
						values={availableMonthsForSelect.map((month) => ({
							value: month,
							label: new Date(0, month - 1)
								.toLocaleString('pt-BR', { month: 'long' })
								.replace(/^\w/, (c) => c.toUpperCase()),
						}))}
					/>
				</div>
				<FormControlLabel
					control={
						<Checkbox
							checked={everythingUntilMayOf26}
							onChange={(e) => setEverythingUntilMayOf26(e.target.checked)}
							sx={{ '& .MuiSvgIcon-root': { fontSize: '1.3rem' } }}
						/>
					}
					disabled={everythingSinceMayOf26}
					labelPlacement="end"
					label="Todas as alterações até Maio/26"
					sx={{ '& .MuiTypography-root': { fontSize: '1rem' } }}
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={everythingSinceMayOf26}
							onChange={(e) => setEverythingSinceMayOf26(e.target.checked)}
							sx={{ '& .MuiSvgIcon-root': { fontSize: '1.3rem' } }}
						/>
					}
					disabled={everythingUntilMayOf26}
					labelPlacement="end"
					label="Todas as alterações a partir de Maio/26"
					sx={{ '& .MuiTypography-root': { fontSize: '1rem' } }}
				/>
			</DialogContent>
			<DialogActions
				sx={{
					width: 'full',
					display: 'flex',
					paddingBottom: '25px',
					gap: '1rem',
					paddingX: '2rem',
				}}
			>
				<Button
					type="button"
					className="min-w-40 border-2 border-[#193CB9] bg-white text-[#193CB9] hover:bg-[#e9eaf1] flex-1"
					onClick={() => setIsOpen(false)}
					disabled={isLoading}
				>
					Cancelar
				</Button>
				<Button
					type="button"
					className={cn(
						'min-w-40 flex-1',
						(isLoading || !isValidSelection) && 'bg-gray-200',
					)}
					onClick={() =>
						onConfirmation(
							monthAndYear,
							everythingSinceMayOf26,
							everythingUntilMayOf26,
						)
					}
					disabled={isLoading || !isValidSelection}
				>
					{isLoading ? (
						<CgSpinner
							size={30}
							color="#4f4f4f"
							className="h-full animate-spin"
						/>
					) : (
						'Confirmar'
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
